import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { isFrameLikeElement } from "@excalidraw/excalidraw/element/typeChecks";
import { presentationSlides } from "@canvaschool/contracts";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";

import { useFrameImages } from "./useFrameImages";
import { fileIdsOfFrame, loadFiles, prepareScene } from "./viewerData";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
} from "@excalidraw/excalidraw/element/types";
import type { BinaryFiles } from "@excalidraw/excalidraw/types";
import type { SerializedExcalidrawElement } from "@canvaschool/contracts";
import type { ApiErrorCode } from "../api/client";
import type { ViewerScene } from "./viewerData";

export type PresentationShowProps = {
  classId: string;
  canvasId: string;
  /** An already-loaded scene (from the editor); otherwise the state and files are fetched once on open. */
  scene?: { elements: readonly ExcalidrawElement[]; files: BinaryFiles };
  onExit(): void;
  /** Teacher only: offered after the last slide and in the controls. */
  next?: { label: string; onNext(): void };
};

/** Controls fade out after this long without pointer movement. */
const CONTROLS_HIDE_MILLISECONDS = 3_000;

const NEXT_KEYS = new Set(["ArrowRight", "PageDown", " ", "Spacebar"]);
const PREVIOUS_KEYS = new Set(["ArrowLeft", "PageUp"]);

type LoadState =
  | { status: "loading" }
  | { status: "failed"; code: ApiErrorCode }
  | { status: "ready"; scene: ViewerScene };

const slidesOf = (
  elements: readonly ExcalidrawElement[],
): ExcalidrawFrameLikeElement[] =>
  presentationSlides(
    elements.filter(
      isFrameLikeElement,
    ) as unknown as SerializedExcalidrawElement[],
  ) as unknown as ExcalidrawFrameLikeElement[];

/**
 * The presentation slideshow (발표): the slides of a presentation canvas one
 * per screen, in slide order (position on the row), full screen. A snapshot
 * like the phone viewer — read once on open, no live updates — and rendered
 * by the same frame pipeline (`useFrameImages`). The editor hands over its
 * live scene; opened from anywhere else, the state is fetched once.
 */
export const PresentationShow = ({
  classId,
  canvasId,
  scene: given,
  onExit,
  next,
}: PresentationShowProps) => {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const { api } = auth;

  const rootRef = useRef<HTMLDivElement>(null);
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [current, setCurrent] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const filesRef = useRef<BinaryFiles>({});
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The scene is taken once, when the show opens: later edits do not
  // reshuffle a running presentation.
  const [snapshot] = useState(() => given ?? null);

  const loadState = useCallback(async () => {
    if (snapshot) {
      filesRef.current = { ...snapshot.files };
      setLoad({
        status: "ready",
        scene: {
          elements: snapshot.elements,
          frames: slidesOf(snapshot.elements),
          assetIds: new Set(),
        },
      });
      return;
    }
    setLoad({ status: "loading" });
    try {
      const state = await api.getCanvasState(classId, canvasId);
      if (!mountedRef.current) {
        return;
      }
      filesRef.current = {};
      const prepared = prepareScene(state);
      setLoad({
        status: "ready",
        scene: { ...prepared, frames: slidesOf(prepared.elements) },
      });
    } catch (error) {
      if (mountedRef.current) {
        setLoad({
          status: "failed",
          code: authRef.current.handleError(error),
        });
      }
    }
  }, [api, classId, canvasId, snapshot]);

  useEffect(() => {
    void loadState();
  }, [loadState]);

  const scene = load.status === "ready" ? load.scene : null;
  const frames = useMemo(() => scene?.frames ?? [], [scene]);
  const total = frames.length;
  const endIndex = next ? total : total - 1;

  const imageSource = useMemo(
    () =>
      scene
        ? {
            elements: scene.elements,
            frames,
            filesRef,
            // Files the editor handed over are ready; only a fetched scene
            // loads (and evicts) its images on demand.
            lazy: snapshot
              ? undefined
              : {
                  fileIdsOf: (frame: ExcalidrawFrameLikeElement) =>
                    fileIdsOfFrame(scene, frame),
                  load: (
                    fileIds: readonly string[],
                    files: BinaryFiles,
                    isCancelled: () => boolean,
                  ) =>
                    loadFiles(
                      api,
                      { classId, canvasId },
                      fileIds,
                      files,
                      isCancelled,
                    ),
                },
          }
        : null,
    [api, classId, canvasId, scene, frames, snapshot],
  );
  const { urls, failedFrames } = useFrameImages(
    imageSource,
    Math.min(current, Math.max(0, total - 1)),
  );

  const go = useCallback(
    (to: number) => setCurrent(Math.max(0, Math.min(endIndex, to))),
    [endIndex],
  );
  const goRef = useRef(go);
  goRef.current = go;
  const currentRef = useRef(current);
  currentRef.current = current;
  const totalRef = useRef(total);
  totalRef.current = total;

  // Full screen on open; a refusal (or no support) leaves the fixed overlay.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    // Keys reach the editor underneath through its focused container.
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }
    root.focus({ preventScroll: true });
    let entered = false;
    const onFullscreenChange = () => {
      if (document.fullscreenElement === root) {
        entered = true;
      } else if (entered) {
        entered = false;
        onExitRef.current();
      }
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    try {
      const request = root.requestFullscreen?.();
      if (request) {
        request.catch(() => {
          // Refused: the overlay already fills the window.
        });
      }
    } catch {
      // Same: fall back to the window-filling overlay.
    }
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      entered = false;
      if (document.fullscreenElement === root) {
        void document.exitFullscreen?.().catch(() => undefined);
      }
    };
  }, []);

  // Keys. In the capture phase on `window`, and swallowed whole: the editor
  // under the show listens on `document` (arrows move a selection, Delete
  // removes it), and paste and cut come in the same way.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      event.stopPropagation();
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      const at = currentRef.current;
      if (event.key === "Escape") {
        event.preventDefault();
        onExitRef.current();
      } else if (NEXT_KEYS.has(event.key)) {
        event.preventDefault();
        goRef.current(at + 1);
      } else if (PREVIOUS_KEYS.has(event.key)) {
        event.preventDefault();
        goRef.current(at - 1);
      } else if (event.key === "Home") {
        event.preventDefault();
        goRef.current(0);
      } else if (event.key === "End") {
        event.preventDefault();
        goRef.current(totalRef.current - 1);
      }
    };
    const swallow = (event: Event) => {
      event.stopPropagation();
      event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    window.addEventListener("keyup", swallow, { capture: true });
    window.addEventListener("paste", swallow, { capture: true });
    window.addEventListener("cut", swallow, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true });
      window.removeEventListener("keyup", swallow, { capture: true });
      window.removeEventListener("paste", swallow, { capture: true });
      window.removeEventListener("cut", swallow, { capture: true });
    };
  }, []);

  // The controls hide after a few quiet seconds and return on any movement.
  const hideTimer = useRef<number | null>(null);
  const wake = useCallback(() => {
    setControlsVisible(true);
    if (hideTimer.current !== null) {
      window.clearTimeout(hideTimer.current);
    }
    hideTimer.current = window.setTimeout(() => {
      hideTimer.current = null;
      setControlsVisible(false);
    }, CONTROLS_HIDE_MILLISECONDS);
  }, []);
  useEffect(() => {
    wake();
    return () => {
      if (hideTimer.current !== null) {
        window.clearTimeout(hideTimer.current);
      }
    };
  }, [wake]);

  const onSurfaceClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const rightHalf = event.clientX >= box.left + box.width / 2;
    go(current + (rightHalf ? 1 : -1));
  };

  const atEnd = next !== undefined && current >= total && total > 0;
  const shown = Math.min(current, total - 1);
  const slideName = (index: number) => `${index + 1}번째 슬라이드`;

  const content = (
    <div
      ref={rootRef}
      className="canvaschool-show"
      role="dialog"
      aria-label="발표"
      aria-modal="true"
      tabIndex={-1}
      onPointerMove={wake}
      onPointerDown={wake}
    >
      {load.status === "loading" && (
        <p className="canvaschool-show-note" aria-busy="true">
          발표를 불러오는 중…
        </p>
      )}
      {load.status === "failed" && (
        <div className="canvaschool-show-note">
          <ErrorAlert code={load.code} />
          <button type="button" onClick={() => void loadState()}>
            다시 시도
          </button>
        </div>
      )}
      {scene && total === 0 && (
        <p className="canvaschool-show-note">보여 줄 슬라이드가 없어요.</p>
      )}
      {scene && total > 0 && !atEnd && (
        // Click targets: the left half goes back, the right half goes on.
        <div
          className="canvaschool-show-slide"
          data-testid="show-surface"
          onClick={onSurfaceClick}
        >
          {urls[frames[shown].id] ? (
            <img
              className="canvaschool-show-image"
              src={urls[frames[shown].id]}
              alt={slideName(shown)}
              draggable={false}
            />
          ) : failedFrames.has(frames[shown].id) ? (
            <p className="canvaschool-show-note">
              이 슬라이드를 그리지 못했어요.
            </p>
          ) : (
            <p className="canvaschool-show-note" aria-busy="true">
              그리는 중…
            </p>
          )}
        </div>
      )}
      {atEnd && next && (
        <div className="canvaschool-show-end">
          <p>발표가 끝났어요.</p>
          <button
            type="button"
            className="canvaschool-show-next"
            onClick={next.onNext}
          >
            {next.label}
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
      )}
      <div
        className={
          controlsVisible
            ? "canvaschool-show-controls"
            : "canvaschool-show-controls canvaschool-show-controls--hidden"
        }
        onClick={(event) => event.stopPropagation()}
      >
        {total > 0 && (
          <div className="canvaschool-show-pager">
            <button
              type="button"
              className="canvaschool-show-icon"
              aria-label="이전 슬라이드"
              disabled={current <= 0}
              onClick={() => go(current - 1)}
            >
              <ChevronLeft size={20} aria-hidden="true" />
            </button>
            <span className="canvaschool-show-counter" aria-live="polite">
              {Math.min(current, total - 1) + 1} / {total}
            </span>
            <button
              type="button"
              className="canvaschool-show-icon"
              aria-label="다음 슬라이드"
              disabled={current >= endIndex}
              onClick={() => go(current + 1)}
            >
              <ChevronRight size={20} aria-hidden="true" />
            </button>
          </div>
        )}
        {next && !atEnd && (
          <button
            type="button"
            className="canvaschool-show-next"
            onClick={next.onNext}
          >
            {next.label}
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className="canvaschool-show-exit"
          onClick={onExit}
        >
          <X size={16} aria-hidden="true" />
          끝내기
        </button>
      </div>
    </div>
  );

  // Under `document.body`, outside `.canvaschool-shell`: the show defines
  // the --cs-* tokens itself (see _tokens.scss) and styles its own buttons.
  return createPortal(content, document.body);
};
